import { COMMENT_BODY_MAX_LENGTH } from '@atlas/api-contract';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';

import { toDisplayableError } from '@/components/states';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import { commentFormSchema, type CommentFormValues } from './thread-schemas';

export interface CommentComposerProps {
  /** Visually hidden, but read out — every composer here sits in a popover. */
  readonly label: string;
  readonly placeholder: string;
  readonly submitLabel: string;
  /** Verb shown while the request is in flight, e.g. "Posting…". */
  readonly pendingLabel: string;
  /** Seeds the field. Used by the edit composer; empty everywhere else. */
  readonly defaultBody?: string;
  readonly autoFocus?: boolean;
  readonly rows?: number;
  /**
   * Performs the mutation.
   *
   * Resolving `true` clears the field — the comment is posted and the composer
   * is ready for the next one. Resolving `false` leaves everything exactly as
   * it is, which is how a cancelled display-name prompt gets out of the way
   * without costing the reviewer their draft. A rejection is rendered above the
   * field, and also leaves the draft alone.
   */
  readonly onSubmit: (body: string) => Promise<boolean>;
  /** Renders a cancel button when present. */
  readonly onCancel?: (() => void) | undefined;
  readonly cancelLabel?: string;
  readonly className?: string;
}

/**
 * The one composer behind every comment written in Atlas: the first comment on
 * a new thread, a reply, and an edit.
 *
 * ============================================================================
 * IT NEVER CLEARS ITSELF ON FAILURE (spec R5.7)
 * ============================================================================
 * A review comment is often the most considered paragraph a person writes all
 * day, typed into a floating box over somebody else's prototype. Losing it to a
 * dropped connection is not a minor annoyance — it is the thing that stops
 * people using a review tool. So the field is only ever cleared on a resolved
 * `true`, the error is rendered *beside* the draft rather than in a toast that
 * takes the popover down with it, and the submit button becomes available again
 * the moment the request settles.
 *
 * ⌘/Ctrl+Enter submits, because the alternative — Enter submitting — would post
 * a comment every time somebody started a second paragraph.
 */
export const CommentComposer = ({
  label,
  placeholder,
  submitLabel,
  pendingLabel,
  defaultBody = '',
  autoFocus = false,
  rows = 3,
  onSubmit,
  onCancel,
  cancelLabel = 'Cancel',
  className,
}: CommentComposerProps) => {
  const form = useForm<CommentFormValues>({
    resolver: zodResolver(commentFormSchema),
    defaultValues: { body: defaultBody },
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      if (await onSubmit(values.body)) {
        form.reset({ body: '' });
      }
    } catch (error: unknown) {
      form.setError('root', {
        type: 'server',
        message: toDisplayableError(error),
      });
    }
  });

  const submit = (): void => {
    void handleSubmit();
  };

  const isSubmitting = form.formState.isSubmitting;
  const rootError = form.formState.errors.root?.message;
  const length = form.watch('body').trim().length;

  return (
    <Form {...form}>
      <form
        noValidate
        className={cn('space-y-2', className)}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {rootError === undefined ? null : (
          <p
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-1.5 text-xs text-destructive"
          >
            {rootError}
          </p>
        )}

        <FormField
          control={form.control}
          name="body"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="sr-only">{label}</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  autoFocus={autoFocus}
                  rows={rows}
                  placeholder={placeholder}
                  maxLength={COMMENT_BODY_MAX_LENGTH}
                  className="resize-none text-sm"
                  onKeyDown={(event) => {
                    if (
                      event.key === 'Enter' &&
                      (event.metaKey || event.ctrlKey)
                    ) {
                      event.preventDefault();
                      submit();
                    }
                  }}
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        <div className="flex items-center gap-2">
          {/*
            The counter appears only once the comment is long enough to be worth
            worrying about. Shown from the first character it would be a running
            commentary on how much someone has typed.
          */}
          <span
            className={cn(
              'text-xs tabular-nums text-muted-foreground',
              length > COMMENT_BODY_MAX_LENGTH * 0.9 ? '' : 'invisible',
            )}
            aria-hidden={length > COMMENT_BODY_MAX_LENGTH * 0.9 ? undefined : 'true'}
          >
            {length} / {COMMENT_BODY_MAX_LENGTH}
          </span>

          <div className="ml-auto flex items-center gap-2">
            {onCancel === undefined ? null : (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={isSubmitting}
                onClick={onCancel}
              >
                {cancelLabel}
              </Button>
            )}
            <Button type="submit" size="sm" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 aria-hidden="true" className="animate-spin" />
                  {pendingLabel}
                </>
              ) : (
                submitLabel
              )}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};
